import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";

import { useAuth } from "@/composables/useAuth";
import { useAppStore } from "@/stores/app";

declare module "vue-router" {
  interface RouteMeta {
    title?: string;
    // Routes flagged public are reachable without an authenticated session.
    public?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/auth/LoginView.vue"),
    meta: { title: "Sign In", public: true },
  },
  {
    path: "/signup",
    name: "signup",
    component: () => import("@/views/auth/SignupView.vue"),
    meta: { title: "Sign Up", public: true },
  },
  {
    path: "/",
    name: "home",
    component: () => import("@/views/HomeView.vue"),
    meta: { title: "Home" },
  },
  {
    path: "/categories",
    name: "categories",
    component: () => import("@/views/categories/CategoriesView.vue"),
    meta: { title: "Categories" },
  },
  {
    path: "/categories/:id",
    name: "category-detail",
    component: () => import("@/views/categories/CategoryDetailView.vue"),
    meta: { title: "Category Detail" },
    props: true,
  },
  {
    path: "/tags",
    name: "tags",
    component: () => import("@/views/tags/TagsView.vue"),
    meta: { title: "Tags" },
  },
  {
    path: "/exercises",
    name: "exercises",
    component: () => import("@/views/exercises/ExercisesView.vue"),
    meta: { title: "Exercises" },
  },
  {
    path: "/exercises/:id",
    name: "exercise-detail",
    component: () => import("@/views/exercises/ExerciseDetailView.vue"),
    meta: { title: "Exercise Detail" },
    props: true,
  },
  {
    path: "/review",
    name: "review",
    component: () => import("@/views/review/ReviewView.vue"),
    meta: { title: "Review" },
  },
  {
    path: "/sessions",
    name: "sessions",
    component: () => import("@/views/sessions/SessionsView.vue"),
    meta: { title: "Practice Sessions" },
  },
  {
    path: "/sessions/new",
    name: "new-session",
    component: () => import("@/views/sessions/NewSessionView.vue"),
    meta: { title: "New Practice Session" },
  },
  {
    path: "/sessions/:id",
    name: "session-detail",
    component: () => import("@/views/sessions/SessionDetailView.vue"),
    meta: { title: "Session Detail" },
    props: true,
  },
  {
    path: "/new-breed",
    name: "new-breed",
    component: () => import("@/views/newbreed/NewBreedView.vue"),
    meta: { title: "The New Breed" },
  },
  {
    path: "/new-breed/system/:id",
    name: "new-breed-system",
    component: () => import("@/views/newbreed/NewBreedSystemView.vue"),
    meta: { title: "New Breed System" },
    props: true,
  },
  {
    path: "/settings",
    name: "settings",
    component: () => import("@/views/settings/SettingsView.vue"),
    meta: { title: "Settings" },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("@/views/NotFoundView.vue"),
    meta: { title: "404 - Not Found" },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0 };
  },
});

// Verify the session once per page load, then gate access to private routes.
let authBootstrap: Promise<unknown> | null = null;

router.beforeEach(async (to) => {
  const appStore = useAppStore();

  // On the first navigation, check any existing session cookie exactly once.
  if (!appStore.authChecked) {
    if (!authBootstrap) {
      authBootstrap = useAuth().refreshSession();
    }
    await authBootstrap;
  }

  const isPublic = to.meta.public === true;
  if (!isPublic && !appStore.isAuthenticated()) {
    return { name: "login", query: { redirect: to.fullPath } };
  }
  if (isPublic && appStore.isAuthenticated()) {
    return { name: "home" };
  }
  return true;
});

router.beforeEach((to, _from, next) => {
  const defaultTitle = "Tempus";
  const routeTitle = typeof to.meta.title === "string" ? to.meta.title : null;
  document.title = routeTitle ? `${routeTitle} | ${defaultTitle}` : defaultTitle;
  next();
});

// Recover from stale lazy-loaded chunks after a redeploy.
//
// Each view is a hash-named chunk loaded via dynamic import. When a tab sits
// idle and the app is redeployed, the already-loaded index.html still points at
// old chunk hashes that no longer exist on the server. The SPA fallback then
// serves index.html (text/html) for the missing .js path, so the dynamic import
// fails with "Failed to fetch dynamically imported module" / a MIME-type error.
//
// When we detect that failure, do a full navigation to the intended route. That
// re-fetches the fresh index.html (with the new chunk hashes) and recovers
// without the user having to manually refresh.
const RELOAD_GUARD_KEY = "tempus:reloaded-for-import-error";

router.onError((error, to) => {
  const message = error instanceof Error ? error.message : String(error ?? "");
  const isStaleChunkError =
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("Unable to preload CSS");

  if (!isStaleChunkError) return;

  // Guard against reload loops: if a full reload already happened and the chunk
  // is still unreachable, the file is genuinely missing — don't keep reloading.
  if (sessionStorage.getItem(RELOAD_GUARD_KEY)) return;

  sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
  window.location.assign(to.fullPath);
});

// A successful navigation means the fresh chunks loaded — clear the guard so a
// future redeploy can trigger recovery again.
router.afterEach(() => {
  sessionStorage.removeItem(RELOAD_GUARD_KEY);
});

export default router;
